import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import toast from 'react-hot-toast';
import {
  CogIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    system: {
      siteName: 'ChatZa',
      siteDescription: 'Comprehensive client management with Chatbot integration',
      maintenanceMode: false,
      registrationEnabled: true,
      emailNotifications: true
    },
    payment: {
      defaultCurrency: 'ZAR',
      supportedCurrencies: ['ZAR', 'USD', 'NGN', 'EUR', 'GBP'],
      paystackEnabled: true,
      paystackTestMode: true
    },
    subscription: {
      autoRenewalEnabled: true,
      gracePeriodDays: 7,
      trialPeriodDays: 0,
      maxSubscriptionsPerUser: 1
    },
    email: {
      smtpHost: 'smtp.gmail.com',
      smtpPort: 587,
      smtpUser: '',
      smtpPass: '',
      fromEmail: 'noreply@chatza.com',
      fromName: 'ChatZa'
    },
    security: {
      sessionTimeout: 24,
      maxLoginAttempts: 5,
      passwordMinLength: 8,
      requireEmailVerification: false
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('system');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/settings');
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', { settings });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const tabs = [
    { id: 'system', name: 'System', icon: CogIcon },
    { id: 'payment', name: 'Payment', icon: CreditCardIcon },
    { id: 'subscription', name: 'Subscription', icon: CurrencyDollarIcon },
    { id: 'email', name: 'Email', icon: EnvelopeIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure system options and preferences
          </p>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          ) : (
            <CheckIcon className="h-4 w-4 mr-2" />
          )}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Settings Content */}
      <div className="bg-white shadow rounded-lg">
        {activeTab === 'system' && (
          <SystemSettings settings={settings.system} updateSetting={(key, value) => updateSetting('system', key, value)} />
        )}
        {activeTab === 'payment' && (
          <PaymentSettings settings={settings.payment} updateSetting={(key, value) => updateSetting('payment', key, value)} />
        )}
        {activeTab === 'subscription' && (
          <SubscriptionSettings settings={settings.subscription} updateSetting={(key, value) => updateSetting('subscription', key, value)} />
        )}
        {activeTab === 'email' && (
          <EmailSettings settings={settings.email} updateSetting={(key, value) => updateSetting('email', key, value)} />
        )}
        {activeTab === 'security' && (
          <SecuritySettings settings={settings.security} updateSetting={(key, value) => updateSetting('security', key, value)} />
        )}
      </div>
    </div>
  );
};

// System Settings Component
const SystemSettings = ({ settings, updateSetting }) => {
  return (
    <div className="p-6 space-y-6">
      <h3 className="text-lg font-medium text-gray-900">System Configuration</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Site Name</label>
          <input
            type="text"
            value={settings.siteName}
            onChange={(e) => updateSetting('siteName', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">Site Description</label>
          <input
            type="text"
            value={settings.siteDescription}
            onChange={(e) => updateSetting('siteDescription', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.maintenanceMode}
              onChange={(e) => updateSetting('maintenanceMode', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span className="ml-2 text-sm text-gray-700">Maintenance Mode</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">Enable maintenance mode to restrict access</p>
        </div>
        
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.registrationEnabled}
              onChange={(e) => updateSetting('registrationEnabled', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span className="ml-2 text-sm text-gray-700">Enable Registration</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">Allow new user registrations</p>
        </div>
        
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.emailNotifications}
              onChange={(e) => updateSetting('emailNotifications', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span className="ml-2 text-sm text-gray-700">Email Notifications</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">Send email notifications to users</p>
        </div>
      </div>
    </div>
  );
};

// Payment Settings Component
const PaymentSettings = ({ settings, updateSetting }) => {
  return (
    <div className="p-6 space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Payment Configuration</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Default Currency</label>
          <select
            value={settings.defaultCurrency}
            onChange={(e) => updateSetting('defaultCurrency', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="USD">USD - US Dollar</option>
            <option value="NGN">NGN - Nigerian Naira</option>
            <option value="EUR">EUR - Euro</option>
            <option value="GBP">GBP - British Pound</option>
          </select>
        </div>
        
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.paystackEnabled}
              onChange={(e) => updateSetting('paystackEnabled', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span className="ml-2 text-sm text-gray-700">Enable Paystack</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">Enable Paystack payment processing</p>
        </div>
        
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.paystackTestMode}
              onChange={(e) => updateSetting('paystackTestMode', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span className="ml-2 text-sm text-gray-700">Paystack Test Mode</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">Use Paystack test environment</p>
        </div>
      </div>
    </div>
  );
};

// Subscription Settings Component
const SubscriptionSettings = ({ settings, updateSetting }) => {
  return (
    <div className="p-6 space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Subscription Configuration</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.autoRenewalEnabled}
              onChange={(e) => updateSetting('autoRenewalEnabled', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span className="ml-2 text-sm text-gray-700">Auto Renewal</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">Enable automatic subscription renewal</p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">Grace Period (Days)</label>
          <input
            type="number"
            value={settings.gracePeriodDays}
            onChange={(e) => updateSetting('gracePeriodDays', parseInt(e.target.value))}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            min="0"
            max="30"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">Trial Period (Days)</label>
          <input
            type="number"
            value={settings.trialPeriodDays}
            onChange={(e) => updateSetting('trialPeriodDays', parseInt(e.target.value))}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            min="0"
            max="30"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">Max Subscriptions Per User</label>
          <input
            type="number"
            value={settings.maxSubscriptionsPerUser}
            onChange={(e) => updateSetting('maxSubscriptionsPerUser', parseInt(e.target.value))}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            min="1"
            max="10"
          />
        </div>
      </div>
    </div>
  );
};

// Email Settings Component
const EmailSettings = ({ settings, updateSetting }) => {
  return (
    <div className="p-6 space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Email Configuration</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">SMTP Host</label>
          <input
            type="text"
            value={settings.smtpHost}
            onChange={(e) => updateSetting('smtpHost', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">SMTP Port</label>
          <input
            type="number"
            value={settings.smtpPort}
            onChange={(e) => updateSetting('smtpPort', parseInt(e.target.value))}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">SMTP Username</label>
          <input
            type="text"
            value={settings.smtpUser}
            onChange={(e) => updateSetting('smtpUser', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">SMTP Password</label>
          <input
            type="password"
            value={settings.smtpPass}
            onChange={(e) => updateSetting('smtpPass', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">From Email</label>
          <input
            type="email"
            value={settings.fromEmail}
            onChange={(e) => updateSetting('fromEmail', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">From Name</label>
          <input
            type="text"
            value={settings.fromName}
            onChange={(e) => updateSetting('fromName', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
    </div>
  );
};

// Security Settings Component
const SecuritySettings = ({ settings, updateSetting }) => {
  return (
    <div className="p-6 space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Security Configuration</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Session Timeout (Hours)</label>
          <input
            type="number"
            value={settings.sessionTimeout}
            onChange={(e) => updateSetting('sessionTimeout', parseInt(e.target.value))}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            min="1"
            max="168"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">Max Login Attempts</label>
          <input
            type="number"
            value={settings.maxLoginAttempts}
            onChange={(e) => updateSetting('maxLoginAttempts', parseInt(e.target.value))}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            min="3"
            max="10"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">Password Min Length</label>
          <input
            type="number"
            value={settings.passwordMinLength}
            onChange={(e) => updateSetting('passwordMinLength', parseInt(e.target.value))}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            min="6"
            max="20"
          />
        </div>
        
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.requireEmailVerification}
              onChange={(e) => updateSetting('requireEmailVerification', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span className="ml-2 text-sm text-gray-700">Require Email Verification</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">Require email verification for new accounts</p>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings; 